import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Edit2, Trash2, DollarSign, Package } from "lucide-react";
import { useModulePrices } from "@/hooks/useModulePrices";
import { ModulePrice } from "@/types/marketplace";
import { EditModuleDialog } from "./EditModuleDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const ModuleCatalogTab = () => {
  const { modulePrices, isLoading, deleteModule, isDeleting, updateModule } = useModulePrices();
  const [editingModule, setEditingModule] = useState<ModulePrice | null>(null);
  const [deletingModule, setDeletingModule] = useState<ModulePrice | null>(null);

  const formatPrice = (price: number) => {
    return price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Carregando módulos...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {modulePrices.map((module) => (
          <Card key={module.id} className={`relative ${!module.is_active ? 'opacity-60' : ''}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    {module.name}
                    {!module.is_active && <Badge variant="secondary">Inativo</Badge>}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Código: <code className="text-xs bg-muted px-1 rounded">{module.code}</code>
                  </CardDescription>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditingModule(module)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeletingModule(module)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {module.description && (
                <p className="text-sm text-muted-foreground">{module.description}</p>
              )}
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <span className="text-lg font-bold">{formatPrice(module.price)}</span>
                <span className="text-sm text-muted-foreground">/mês por licença</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <Switch
                  checked={module.is_active}
                  onCheckedChange={(checked) =>
                    updateModule({ id: module.id, data: { is_active: checked } })
                  }
                />
              </div>
              {module.stripe_price_id && (
                <div className="text-xs text-muted-foreground">
                  Stripe: {module.stripe_price_id}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {modulePrices.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhum módulo cadastrado</h3>
            <p className="text-muted-foreground">Clique em "Novo Módulo" para criar o primeiro módulo</p>
          </CardContent>
        </Card>
      )}

      <EditModuleDialog
        module={editingModule}
        open={!!editingModule}
        onOpenChange={(open) => !open && setEditingModule(null)}
      />

      <AlertDialog open={!!deletingModule} onOpenChange={(open) => !open && setDeletingModule(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza de que deseja excluir o módulo "{deletingModule?.name}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingModule) {
                  deleteModule(deletingModule.id);
                  setDeletingModule(null);
                }
              }}
              disabled={isDeleting}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
